/**
 * artemisCursorMetrics — the bow cursor's painted geometry, as constants.
 *
 * `public/artemis-bow-cursor.svg` is the cursor image every whitelisted
 * Artemis subtree wears (the `.artemis-cursor-target` rule in
 * `app/frontend/assets/base.css`). Unlike the OS arrow, its bow-and-arrow
 * badge paints well down-right of the hotspot — far enough to sit on top of
 * anything placed at a small offset from the pointer, which is exactly where
 * cursor-follow tooltips live. `GlanceCardCursor` derives its horizontal
 * pointer offset from these metrics so the card's top-left corner always
 * clears the glyph.
 *
 * The extents are a CONTRACT with the SVG (its header comment points back
 * here): they are the ceil'd painted bounding box of the glyph — pointer
 * arrow + badge + halo strokes — in the SVG's 32×32 coordinate space.
 * Redrawing the cursor means re-deriving them in the same change, and
 * `__tests__/artemisCursorMetrics.svgPin.test.js` parses the SVG's own
 * coordinates to fail when that does not happen.
 *
 * The cursor-ANCHORED boxes — the dwell ring and the hold badge — sit here too,
 * because each is sized and offset against the glyph this module measures, and
 * because `composables/overlay/overlayKinds.js` reads them as the two `cue`
 * kinds' placement offsets. Only the extents are SVG-pinned; each box says so
 * where it is declared.
 */

// The CSS `cursor: url(...) 1 1` hotspot — where the pointer actually is,
// relative to the SVG origin.
export const BOW_CURSOR_HOTSPOT = { x: 1, y: 1 };

// Ceil'd painted bounding box of the glyph relative to the SVG origin
// (halo strokes included). Current geometry: the arrow-shaft tip at x=21
// plus its 2.4-wide halo → 22.2; the lower string attachment at y=22 plus
// halo → 23.2.
export const BOW_CURSOR_PAINTED_EXTENT = { right: 23, down: 24 };

// Horizontal pointer→card clearance for cursor-follow tooltip adapters: the
// glyph's reach beyond the hotspot, plus a breathing gap. Clearing ONE axis
// is sufficient (overlap needs both), and the horizontal axis keeps the card
// vertically tight beside the glyph. The gap is 6, not the 2 the extent math
// alone suggests: the arrowhead's miter tips and the shaft's square caps
// overshoot the path points by ~2px, and browser-UAT (2026-08-18) showed a
// 2px gap still reads as touching once cursor antialiasing is on top.
export const BOW_CURSOR_CARD_CLEARANCE_X =
  BOW_CURSOR_PAINTED_EXTENT.right - BOW_CURSOR_HOTSPOT.x + 6;

// Box side of the glance dwell ring, in CSS pixels. The ring's circle is
// inscribed in this box, so the box side is also the circle's diameter.
export const GLANCE_DWELL_RING_PX = 14;

// --- The hold bow's own badge, which is NOT the cursor glyph ----------------
//
// The two constants below describe the 48 px badge `WorkspaceShell` paints
// while an Artemis cursor-hold runs — a surface of its own, drawn up-left of
// the pointer. They are NOT pinned to `artemis-bow-cursor.svg` the way the
// extents above are; they live here because the badge is cursor-anchored
// geometry and `overlayKinds.js` cites the gap as the `artemis-bow` cue's
// offset, exactly as it cites BOW_CURSOR_CARD_CLEARANCE_X.

// Box side of the hold badge, in CSS pixels: the ring of chrome around the
// ArtemisBow glyph (`w-12 h-12`), which is the box the engine places.
export const BOW_OVERLAY_PX = 48;

// Buffer between the badge's lower-right corner and the pointer hotspot, so the
// badge clears the cursor glyph that is being held.
export const BOW_OVERLAY_GAP = 6;

// Centre of the dwell ring relative to the pointer hotspot — the cursor's
// up-left SHOULDER, the one quadrant no live cursor paints into.
//
// Derivation. In hotspot-relative coordinates the glyph's painted box is
// BOW_CURSOR_PAINTED_EXTENT minus BOW_CURSOR_HOTSPOT, with the halo path's
// `M 0 0` supplying the near corner: x in [-1, +22], y in [-1, +23]. A ring
// centred at (-8, -8) with a 14px box spans [-15, -1] on both axes, so its
// box is edge-ADJACENT to the glyph's — the two share the -1 edge and
// overlap nowhere. Adjacency is why the clearance assertion is written `<=`
// and not `<`: equality at the shared edge is contact, not intersection.
// The painted circle is slacker still: the nearest glyph corner (-1, -1)
// sits sqrt(7^2 + 7^2) ~= 9.9 from the centre, ~3px outside the circle's
// 7px outer edge.
//
// The shoulder generalizes past the bow. Every cursor a glance surface can
// wear — this bow, the OS arrow, the map's grab hand — hangs down-right of
// its hotspot, so up-left of the hotspot is clear on all of them.
export const CURSOR_SHOULDER_ANCHOR = { x: -8, y: -8 };
